import { JobAlert, JobType } from '../../types';

const normalize = (s: string): string => s.toLowerCase();

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ');

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A term matches when it equals one of the job's tags or appears as a whole
// token in the job text (so "java" doesn't match inside "javascript").
const termMatches = (term: string, tagSet: Set<string>, text: string): boolean => {
  if (tagSet.has(term)) return true;
  return new RegExp(`(^|[^a-z0-9])${escapeRegExp(term)}([^a-z0-9]|$)`, 'i').test(text);
};

const jobText = (job: JobType): string =>
  normalize(
    [
      job.position ?? '',
      (job.tags ?? []).join(' '),
      stripHtml((job.description ?? '').slice(0, 3000)),
    ].join(' ')
  );

// Does a job satisfy an alert's criteria? The query terms are ANDed (every
// word must be present) and the tag filter is ORed (the job must carry at
// least one of the alert's tags). An alert with no criteria matches nothing
// (we never spam users with everything). `enabled` is the caller's concern.
export const jobMatchesAlert = (job: JobType, alert: JobAlert): boolean => {
  const queryTerms = normalize(alert.query ?? '')
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const alertTags = (alert.tags ?? []).map((t) => normalize(t).trim()).filter(Boolean);

  if (queryTerms.length === 0 && alertTags.length === 0) return false;

  const tagSet = new Set((job.tags ?? []).map((t) => normalize(t).trim()).filter(Boolean));
  const text = jobText(job);

  const queryOk = queryTerms.every((term) => termMatches(term, tagSet, text));
  const tagsOk = alertTags.length === 0 || alertTags.some((t) => termMatches(t, tagSet, text));
  return queryOk && tagsOk;
};

// Add dummy default export for Expo Router (every file under app/ needs one).
export default { jobMatchesAlert };
